import { useQuery } from "@tanstack/react-query";
import {
  CellContext,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { KeywordImport } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import Breadcrumbs from "../../components/ui/breadcrumbs/Breadcrumbs";
import {
  Pagination,
  PaginationConfig,
} from "../../components/ui/pagination/Pagination";
import styles from "./KeywordImportPage.module.scss";

const KeywordImportsPage = () => {
  usePageTitle(PAGE_TITLES.keywordImports);

  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });
  const { id } = useParams();

  if (!id) {
    throw Error("No site id provided");
  }

  const { data: keywordImports, isLoading } = useQuery({
    queryKey: [
      "keywordImports",
      id,
      pagination.itemsPerPage,
      pagination.currentPage,
    ],
    queryFn: async () => {
      const response = await apiClient.sites.keywordImportsDetail(Number(id), {
        "page[size]": pagination.itemsPerPage,
        "page[number]": pagination.currentPage,
        s: "created_at DESC",
      });
      const { headers } = response;

      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count")),
        nextPage: Number(headers.get("x-pager-next-page")),
        prevPage: Number(headers.get("x-pager-prev-page")),
        totalPages: Number(headers.get("x-pager-total-pages")),
      }));

      return response.data.keyword_imports;
    },
    refetchInterval(query) {
      const data = query?.state?.data;
      if (data && Array.isArray(data)) {
        for (const item of data) {
          if (item.status === "pending") {
            return 3000;
          }
        }
      }

      return false;
    },
  });

  const fakeBreadcrumbs = [
    {
      title: PAGE_TITLES.keywordsList,
      link: routes.site.keywords.list({ id }),
    },
    {
      title: PAGE_TITLES.keywordImports,
      link: routes.site.keywords.imports({ id }),
    },
  ];

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Import ID",
        cell: (info: CellContext<KeywordImport, string>) => {
          const id = info?.getValue?.() || "";

          return <Link to={`${id}`}>#{id}</Link>;
        },
      },
      {
        accessorKey: "created_at",
        header: "Date",
        cell: (info: CellContext<KeywordImport, string>) => {
          const date = info?.getValue?.();

          return date ? format(new Date(date), "MMM d, yyyy h:mm a") : "-";
        },
        sortingFn: "datetime",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) => {
          const status = info?.getValue?.();
          if (status === "complete") {
            return (
              <span className={styles.greenTag}>{status || "unknown"}</span>
            );
          } else if (status === "failed") {
            return <span className={styles.redTag}>{status || "unknown"}</span>;
          } else if (status === "pending") {
            return (
              <span className={styles.blueTag}>{status || "unknown"}</span>
            );
          } else {
            return (
              <span className={styles.blueTag}>{status || "unknown"}</span>
            );
          }
        },
      },
      {
        accessorFn: (row: KeywordImport) =>
          row?.keyword_attributes?.language || "N/A",
        id: "language",
        header: "Language",
      },
      {
        accessorFn: (row: KeywordImport) =>
          row?.keyword_attributes?.country || "N/A",
        id: "country",
        header: "Country",
      },
    ],
    [],
  );

  const table = useReactTable({
    data: keywordImports || [],
    // @ts-expect-error BE types
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
  });

  const renderSkeleton = () => {
    return Array.from({ length: 10 }).map((_, rowIndex) => (
      <tr key={`skeleton-row-${rowIndex}`} className="animate-pulse">
        {Array.from({ length: columns.length }).map((_, colIndex) => (
          <td
            key={`skeleton-cell-${rowIndex}-${colIndex}`}
            className="px-6 py-4 whitespace-nowrap"
          >
            <Skeleton className="h-6 w-full" />
          </td>
        ))}
      </tr>
    ));
  };

  const handlePageChange = (page: number) =>
    setPagination({ ...pagination, currentPage: page });

  const handleItemsPerPageChange = (items: number) =>
    setPagination({ ...pagination, itemsPerPage: items, currentPage: 1 });

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{PAGE_TITLES.keywordImports}</h1>
      <div className={styles.pageBreadcrumbs}>
        {fakeBreadcrumbs.length && (
          <Breadcrumbs fakeBreadcrumbs={fakeBreadcrumbs} />
        )}
      </div>
      <div className={styles.pageContent}>
        <div className={styles.importKeywordTable}>
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} role="columnheader">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                renderSkeleton()
              ) : (
                <>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {table.getRowModel().rows.length === 0 && (
                    <tr>
                      <td colSpan={columns.length}>No keyword imports found</td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
          <Pagination
            {...pagination}
            disabled={isLoading}
            totalItems={pagination.totalItems}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </div>
    </div>
  );
};
export default KeywordImportsPage;
